/** @format */

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import COLORS from '../../../assets/themes/OPL/colors';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import MetricTypeSelector, { MetricType, MetricTypeConfig } from './components/MetricTypeSelector';
import QueueMultiSelect from './components/QueueMultiSelect';
import ChartCard from './components/ChartCard';
import I18n from '../../../core/i18n';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface QueueData {
    queue_name: string;
    reseller_name: string;
    block_count: number;
}

interface MetricResponse {
    '@context': string;
    '@id': string;
    '@type': string;
    'hydra:member': Array<{
        '@id': string;
        '@type': string;
        data: string;
    }>;
    'hydra:totalItems': number;
}

const PREDEFINED_QUEUES = ['activation_transport', 'synchronize_transport', 'common_transport'];
const FTP_QUEUES = ['created', 'uploaded'];
const ACME_QUEUES = [
    'NEW',
    'REJECTED',
    'DISCARDED',
    'SELECTED_FOR_PROCESSING',
    'ADMIN_MAIL_SENT',
    'APPROVED_BY_ADMIN',
    'APPROVED',
];

const METRIC_TYPES: MetricTypeConfig[] = [
    { type: 'W01', label: 'Kolejki' },
    { type: 'W04', label: 'ACME' },
    { type: 'W05', label: 'FTP' },

];

const dataLabelsPlugin = {
    id: 'dataLabels',
    afterDatasetsDraw: (chart: any): void => {
        const ctx = chart.ctx;
        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((bar: any, index: number) => {
                const value = Number(dataset.data[index]);
                if (value === 0 || isNaN(value)) {
                    return;
                }

                ctx.save();
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = COLORS.MAIN.LIGHT;
                ctx.font = 'bold 12px Arial';

                const formattedValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString();

                ctx.fillText(formattedValue, bar.x + 5, bar.y);
                ctx.restore();
            });
        });
    },
};

const BlockQueueDashboard: React.FC = () => {
    const dispatch = useDispatch();
    let delayed = true;
    const [selectedMetricType, setSelectedMetricType] = useState<MetricType | null>(null);
    const [selectedQueues, setSelectedQueues] = useState<string[]>([
        'activation_transport',
        'synchronize_transport',
    ]);
    const [selectedFtpQueues, setSelectedFtpQueues] = useState<string[]>(['created', 'uploaded']);
    const [selectedAcmeQueues, setSelectedAcmeQueues] = useState<string[]>([
        'NEW',
        'REJECTED',
        'APPROVED',
    ]);
    const [queueData, setQueueData] = useState<QueueData[]>([]);
    const [cachedData, setCachedData] = useState<Record<MetricType, QueueData[]>>({
        W01: [],
        W04: [],
        W05: [],
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    const loadMetrics = useCallback(
        (metricType: MetricType, forceRefresh = false, silent = false): void => {
            if (!forceRefresh && cachedData[metricType].length > 0) {
                setQueueData(cachedData[metricType]);
                return;
            }

            if (!silent) {
                setLoading(true);
            }
            const itemLoader = new ItemLoaders(dispatch);

            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/metrics')),
                'metrics_list',
                [{ name: 'type', value: metricType }],
                [],
                (response: MetricResponse) => {
                    try {
                        const parsedData: QueueData[] = response['hydra:member'].map((item) => {
                            const data = JSON.parse(item.data);
                            console.log(data);
                            return {
                                queue_name: data.queueName,
                                reseller_name: data.resellerName,
                                block_count: Number(data.blockCount),
                            };
                        });

                        setCachedData((prev) => ({
                            ...prev,
                            [metricType]: parsedData,
                        }));

                        if (selectedMetricType === metricType) {
                            setQueueData(parsedData);
                        }

                        if (!silent) {
                            setLoading(false);
                        }
                    } catch (error) {
                        console.error('Error parsing metrics data:', error);
                        if (!silent) {
                            setLoading(false);
                        }
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [cachedData, selectedMetricType, dispatch]
    );

    useEffect(() => {
        if (!isInitialized) {
            METRIC_TYPES.forEach((metric) => {
                loadMetrics(metric.type, false, true);
            });
            setIsInitialized(true);
        }
    }, [isInitialized, loadMetrics]);

    const handleMetricTypeChange = (metricType: MetricType): void => {
        setSelectedMetricType(metricType);
        loadMetrics(metricType);
    };

    const handleReload = (): void => {
        if (selectedMetricType) {
            loadMetrics(selectedMetricType, true);
        }
    };

    const handleQueueSelection = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedQueues(event.target.value as string[]);
    };

    const handleFtpQueueSelection = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedFtpQueues(event.target.value as string[]);
    };

    const handleAcmeQueueSelection = (event: React.ChangeEvent<{ value: unknown }>): void => {
        setSelectedAcmeQueues(event.target.value as string[]);
    };

    const getUniqueQueueNames = (): string[] => {
        return PREDEFINED_QUEUES;
    };

    const getDataForQueue = (queueName: string): any => {
        const filteredData = queueData.filter((item) => item.queue_name === queueName);

        return {
            labels: filteredData.map((item) => item.reseller_name),
            datasets: [
                {
                    label: 'Block Count',
                    data: filteredData.map((item) => item.block_count),
                    backgroundColor: COLORS.MAIN.PRIMARY,
                    hoverBackgroundColor: COLORS.MAIN.SECONDARY,
                    borderColor: COLORS.MAIN.PRIMARY,
                    borderWidth: 1,
                    barThickness: 25,
                    maxBarThickness: 30,
                },
            ],
        };
    };

    const getChartOptions = (): any => {
        return {
            responsive: true,
            indexAxis: 'y',
            maintainAspectRatio: false,
            layout: {
                padding: {
                    right: 50,
                },
            },
            animation: {
                onComplete: (): any => {
                    delayed = true;
                },
                delay: (context: any): any => {
                    let delay = 0;
                    if (context.type === 'data' && context.mode === 'default' && !delayed) {
                        delay = context.dataIndex * 300 + context.datasetIndex * 100;
                    }
                    return delay;
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            scales: {
                x: {
                    grid: {
                        display: true,
                        color: '#333',
                    },
                    ticks: {
                        color: COLORS.MAIN.LIGHT,
                        font: {
                            size: 11,
                        },
                        callback: function (value: any): string {
                            return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value.toLocaleString();
                        },
                    },
                    beginAtZero: true,
                },
                y: {
                    grid: {
                        display: false,
                    },
                    ticks: {
                        color: COLORS.MAIN.LIGHT,
                        font: {
                            size: 12,
                            weight: '500',
                        },
                        padding: 8,
                        autoSkip: false,
                    },
                },
            },
        };
    };

    const uniqueQueues = getUniqueQueueNames();

    return (
        <div style={{ padding: '20px' }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <MetricTypeSelector
                        metricTypes={METRIC_TYPES}
                        selectedMetricType={selectedMetricType}
                        loading={loading}
                        onMetricTypeChange={handleMetricTypeChange}
                        onReload={handleReload}
                    />
                </Grid>

                {loading && (
                    <Grid item xs={12}>
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <CircularProgress style={{ color: COLORS.MAIN.PRIMARY }} />
                            <div style={{ marginTop: '20px', color: COLORS.MAIN.LIGHT }}>
                                {I18n.t("specialized_report:dashboard.loading")}
                            </div>
                        </div>
                    </Grid>
                )}

                {selectedMetricType === 'W01' && !loading && (
                    <Grid item xs={12}>
                        <QueueMultiSelect
                            titleTransKey="specialized_report:dashboard.queue_selection.W01"
                            labelId="queue-select-label"
                            selectId="queue-select"
                            queues={uniqueQueues}
                            selectedQueues={selectedQueues}
                            onQueueChange={handleQueueSelection}
                        />
                    </Grid>
                )}

                {selectedMetricType === 'W04' && !loading && (
                    <Grid item xs={12}>
                        <QueueMultiSelect
                            titleTransKey="specialized_report:dashboard.queue_selection.W04"
                            labelId="acme-queue-select-label"
                            selectId="acme-queue-select"
                            queues={ACME_QUEUES}
                            selectedQueues={selectedAcmeQueues}
                            onQueueChange={handleAcmeQueueSelection}
                        />
                    </Grid>
                )}

                {selectedMetricType === 'W05' && !loading && (
                    <Grid item xs={12}>
                        <QueueMultiSelect
                            titleTransKey="specialized_report:dashboard.queue_selection.W05"
                            labelId="ftp-queue-select-label"
                            selectId="ftp-queue-select"
                            queues={FTP_QUEUES}
                            selectedQueues={selectedFtpQueues}
                            onQueueChange={handleFtpQueueSelection}
                        />
                    </Grid>
                )}

                {selectedMetricType === 'W01' &&
                    !loading &&
                    selectedQueues.map((queueName) => {
                        const chartData = getDataForQueue(queueName);
                        const totalBlocks = chartData.datasets[0].data.reduce(
                            (acc: number, val: number) => acc + val,
                            0
                        );
                        const hasData = chartData.labels.length > 0;
                        const chartHeight = Math.max(400, chartData.labels.length * 40);

                        return (
                            <Grid item xs={12} key={queueName}>
                                <ChartCard
                                    title={I18n.t(`specialized_report:dashboard.queues.${queueName}`)}
                                    totalBlocks={totalBlocks}
                                    chartData={chartData}
                                    chartOptions={getChartOptions()}
                                    chartPlugins={[dataLabelsPlugin]}
                                    hasData={hasData}
                                    chartHeight={chartHeight}
                                />
                            </Grid>
                        );
                    })}

                {selectedMetricType === 'W05' &&
                    !loading &&
                    selectedFtpQueues.map((queueName) => {
                        const chartData = getDataForQueue(queueName);
                        const totalBlocks = chartData.datasets[0].data.reduce(
                            (acc: number, val: number) => acc + val,
                            0
                        );
                        const hasData = chartData.labels.length > 0;
                        const chartHeight = Math.max(400, chartData.labels.length * 40);

                        return (
                            <Grid item xs={12} key={queueName}>
                                <ChartCard
                                    title={`${I18n.t('specialized_report:dashboard.metric_types.W05')} - ${I18n.t(`specialized_report:dashboard.queues.${queueName}`)}`}
                                    totalBlocks={totalBlocks}
                                    chartData={chartData}
                                    chartOptions={getChartOptions()}
                                    chartPlugins={[dataLabelsPlugin]}
                                    hasData={hasData}
                                    chartHeight={chartHeight}
                                />
                            </Grid>
                        );
                    })}

                {selectedMetricType === 'W04' && !loading && ((): any => {
                    const filteredData = queueData.filter((item) =>
                        selectedAcmeQueues.includes(item.queue_name)
                    );

                    const chartData = {
                        labels: filteredData.map((item) =>
                            I18n.t(`specialized_report:dashboard.queues.${item.queue_name}`)
                        ),
                        datasets: [
                            {
                                label: 'Block Count',
                                data: filteredData.map((item) => item.block_count),
                                backgroundColor: COLORS.MAIN.PRIMARY,
                                hoverBackgroundColor: COLORS.MAIN.SECONDARY,
                                borderColor: COLORS.MAIN.PRIMARY,
                                borderWidth: 1,
                                barThickness: 25,
                                maxBarThickness: 30,
                            },
                        ],
                    };

                    const totalBlocks = filteredData.reduce((acc, item) => acc + item.block_count, 0);
                    const hasData = filteredData.length > 0;
                    const chartHeight = Math.max(400, filteredData.length * 40);

                    return (
                        <Grid item xs={12}>
                            <ChartCard
                                title={I18n.t('specialized_report:dashboard.chart_titles.acme')}
                                totalBlocks={totalBlocks}
                                chartData={chartData}
                                chartOptions={getChartOptions()}
                                chartPlugins={[dataLabelsPlugin]}
                                hasData={hasData}
                                chartHeight={chartHeight}
                            />
                        </Grid>
                    );
                })()}
            </Grid>
        </div>
    );
};

export default BlockQueueDashboard;
